import { usePropertyStructureType } from '../../../queries/PropertyFields/TopLevelFields';
import { usePropertyGeneralContractorWillWork } from '../../../queries/PropertyFields/NonRentalFlagFields';
import { usePropertyContactIsPrimary } from '../../../queries/PropertyFields/NonRentalContactFields';
import { usePropertyRentalIsAcquisitionFinancing } from '../../../queries/PropertyFields/RentalFlagFields';
import { usePropertySpecSheetFile } from '../../../queries/PropertyFileFields/NonRentalFiles';

import { ShowIf } from '../../components/ShowIf';
import { RentalUnits } from '../Rental/components/RentalUnits/RentalUnits';
import { PropertyAddress } from '../components/PropertyAddress/PropertyAddress';
import { LegalDescription } from '../components/LegalDescription';
import { LandSize } from '../components/LandSize';
import { SiteBuiltOrManufacturedRental } from '../Rental/components/SiteBuiltOrManufacturedRental';
import { GeneralContractor } from '../components/GeneralContractor';
import { GeneralContractorCompany } from '../components/GeneralContractorCompany';
import { GeneralContractorFile } from '../../components/FileOrFormQuestions/questions/GeneralContractorFile';
import { OnsitePropertyContact } from '../components/OnsitePropertyContact';
import { BudgetFile } from '../../components/FileOrFormQuestions/questions/BudgetFile';
import { PlansFile } from '../../components/FileOrFormQuestions/questions/PlansFile';
import { SurveyFile } from '../../components/FileOrFormQuestions/questions/SurveyFile';
import { PurchaseContractFile } from '../../components/FileOrFormQuestions/questions/PurchaseContractFile';
import { CurrentDebtRental } from '../Rental/components/CurrentDebtRental';
import { TargetLoanRequestRental } from '../Rental/components/TargetLoanRequestRental';
import { PreferredTerm } from '../Rental/components/PreferredTerm';
import { StructureAndRentalUnits } from '../Rental/components/StructureAndRentalUnits';
import { SpecSheetFile } from '../../components/FileOrFormQuestions/questions/SpecSheetFile';
import { BuildRentTypeSelect } from './components/BuildRentTypeSelect';
import { OnsitePropertyContactInformation } from '../components/OnsitePropertyContactInformation/OnsitePropertyContactInformation';
import { ProvideLaterAdvisory } from '../components/ProvideLaterAdvisory';
import { ShortTermRentalLease } from '../components/ShortTermRentalLease';
import { MonthsToAccessFunding } from './components/MonthsToAccessFunding';
import { usePropertyRentalPreferredTerm } from '../../../queries/PropertyFields/RentalCoreFields';
import { PreferredTermType } from '@apply/constants';
import { AcquisitionFinancingRental } from '../Rental/components/AcquisitionFinancingRental';

export interface BuildRentProps {
    propertyGuid: string;
    disableEditFormOptionBudget: boolean;
    disableEditFormOptionSpecSheet: boolean;
    isReview?: boolean;
}

export const BuildRent: React.FC<BuildRentProps> = ({
    propertyGuid,
    disableEditFormOptionBudget,
    disableEditFormOptionSpecSheet,
    isReview = false,
}) => {
    const [structureType] = usePropertyStructureType(propertyGuid);
    const [preferredTerm] = usePropertyRentalPreferredTerm(propertyGuid);
    const [generalContractorWillWork] = usePropertyGeneralContractorWillWork(propertyGuid);
    const [contactIsPrimary] = usePropertyContactIsPrimary(propertyGuid);
    const [acquisitionToggle] = usePropertyRentalIsAcquisitionFinancing(propertyGuid);
    const { handleMode: specSheetHandle } = usePropertySpecSheetFile(propertyGuid);

    return (
        <>
            <PropertyAddress
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Additional Information</div>
            </ShowIf>
            <LegalDescription
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <BuildRentTypeSelect
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <LandSize
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <br />
            <br />
            <div className='pageSubHeader'>Improvements</div>
            <SiteBuiltOrManufacturedRental
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShortTermRentalLease
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <GeneralContractor
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={generalContractorWillWork}>
                <br />
                <br />
                <div className='pageSubHeader'>General Contractor</div>
                <GeneralContractorCompany
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
                <GeneralContractorFile
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <ShowIf showIf={!generalContractorWillWork}>
                <OnsitePropertyContact
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <ShowIf showIf={!generalContractorWillWork && contactIsPrimary === false}>
                <OnsitePropertyContactInformation
                    propertyGuid={propertyGuid}
                    isReview={isReview}
                    required={true}
                />
            </ShowIf>
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Additional Files</div>
            </ShowIf>
            <BudgetFile
                propertyGuid={propertyGuid}
                required={true}
                disableEditFormOptionBudget={disableEditFormOptionBudget}
                isReview={isReview}
            />
            <PlansFile
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <SpecSheetFile
                propertyGuid={propertyGuid}
                required={true}
                disableEditFormOptionSpecSheet={disableEditFormOptionSpecSheet}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview && specSheetHandle === 'willnotprovide'}>
                <p style={{ color: 'red', marginLeft: 20 }}>
                    NOTE: Without this detail, the appraiser is likely to assume the lowest grade materials.
                </p>
            </ShowIf>
            <SurveyFile
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <AcquisitionFinancingRental
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={acquisitionToggle === true && acquisitionToggle !== undefined}>
                <PurchaseContractFile
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <br />
            <CurrentDebtRental
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ProvideLaterAdvisory
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <TargetLoanRequestRental
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <PreferredTerm
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={preferredTerm == PreferredTermType.Thirty}>
                <MonthsToAccessFunding
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <StructureAndRentalUnits
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            {structureType && (
                <RentalUnits
                    propertyGuid={propertyGuid}
                    header={'Rental Information'}
                    includeOccupied={false}
                    isReview={isReview}
                />
            )}
        </>
    );
};
